<template>
  <div class="keyboard_appearance_panel scrollbar_thumb">
    <div class="keyboard_appearance_deck">
      <div class="keyboard_appearance_primary_grid">
        <div class="keyboard_appearance_section keyboard_appearance_section_palette">
          <div class="palette_grid">
            <button v-for="option in keyboardColorPresetOptions" :key="option.value" type="button" class="palette_card" :class="{ active: virtualKeyboardAppearance.colorPreset === option.value }" @click="setKeyboardColorPreset(option.value)">
              <div class="palette_preview_v2" :style="{ backgroundColor: option.colors.boardBg }">
                <span class="v2_key" :style="{ backgroundColor: option.colors.keyBg }"></span>
                <span class="v2_key accent" :style="{ backgroundColor: option.colors.accent }"></span>
                <span class="v2_key" :style="{ backgroundColor: option.colors.keyBg }"></span>
              </div>
              <div class="palette_name">{{ $t(option.labelKey) }}</div>
            </button>

            <button type="button" class="palette_card" :class="{ active: virtualKeyboardAppearance.colorPreset === 'custom' }" @click="setKeyboardColorPreset('custom')">
              <div class="palette_preview_v2" :style="{ backgroundColor: virtualKeyboardAppearance.customColors.board }">
                <span class="v2_key" :style="{ backgroundColor: virtualKeyboardAppearance.customColors.key }"></span>
                <span class="v2_key accent" :style="{ backgroundColor: virtualKeyboardAppearance.customColors.accent }"></span>
                <span class="v2_key" :style="{ backgroundColor: virtualKeyboardAppearance.customColors.key }"></span>
              </div>
              <div class="palette_name">{{ $t('keyboard_color_custom') }}</div>
            </button>
          </div>
        </div>
      </div>

      <div class="keyboard_appearance_section keyboard_appearance_section_custom">
        <div class="section_title">{{ $t('keyboard_custom_colors') }}</div>
        <div class="custom_color_grid">
          <label v-for="field in keyboardCustomColorFields" :key="field.key" class="color_control_card">
            <span class="control_label">{{ $t(field.labelKey) }}</span>
            <span class="control_picker">
              <span class="control_swatch" :style="{ backgroundColor: virtualKeyboardAppearance.customColors[field.key] }"></span>
              <input class="native_color_input" type="color" :value="virtualKeyboardAppearance.customColors[field.key]" @input="handleColorInput(field.key, $event)" />
            </span>
            <input
              class="native_hex_input"
              type="text"
              :value="colorDrafts[field.key]"
              maxlength="7"
              spellcheck="false"
              @input="handleColorDraftInput(field.key, $event)"
              @blur="commitColorDraft(field.key)"
              @keydown.enter.prevent="commitColorDraft(field.key)"
            />
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, watch } from 'vue'
import { keyboardColorPresetOptions, keyboardCustomColorFields, setKeyboardColorPreset, setKeyboardCustomColor, virtualKeyboardAppearance, KeyboardCustomColorKey } from '@/service/keyboard_appearance'

const colorDrafts = reactive<Record<KeyboardCustomColorKey, string>>({
  ...virtualKeyboardAppearance.customColors,
})

watch(
  () => virtualKeyboardAppearance.customColors,
  (colors) => {
    ;(Object.keys(colors) as KeyboardCustomColorKey[]).forEach((key) => {
      colorDrafts[key] = colors[key]
    })
  },
  { deep: true },
)

const handleColorInput = (key: KeyboardCustomColorKey, event: Event) => {
  const target = event.target as HTMLInputElement | null
  if (!target) {
    return
  }

  setKeyboardCustomColor(key, target.value)
}

const handleColorDraftInput = (key: KeyboardCustomColorKey, event: Event) => {
  const target = event.target as HTMLInputElement | null
  if (!target) {
    return
  }

  colorDrafts[key] = target.value.trim()
}

const commitColorDraft = (key: KeyboardCustomColorKey) => {
  setKeyboardCustomColor(key, colorDrafts[key])
  colorDrafts[key] = virtualKeyboardAppearance.customColors[key]
}
</script>

<style scoped lang="less">
/* 键盘外观面板始终在 Floating 外壳内渲染（紧凑布局），header/auto-saved/reset 由 AppearanceFloating 提供。 */
.keyboard_appearance_panel {
  height: 100%;
  overflow-y: auto;
  padding: 16px 18px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.keyboard_appearance_deck {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: min(100%, var(--keyboard-appearance-content-width, 860px));
  margin: 0 auto;
}

.keyboard_appearance_primary_grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 12px;
}

.keyboard_appearance_section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: var(--radius-l);
  background: var(--glass-surface-bg);
  backdrop-filter: var(--glass-surface-blur);
  border: 1px solid rgba(255, 255, 255, 0.48);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.04);

  .section_title {
    font-size: 14px;
    font-weight: 700;
    color: var(--surface-text-primary);
  }
}

.palette_grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.custom_color_grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.palette_card {
  border: none;
  padding: 12px;
  border-radius: var(--radius-m);
  background: rgba(255, 255, 255, 0.72);
  cursor: pointer;
  transition: var(--transition-base);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.04);
  min-height: 92px;
  min-width: 0;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  }

  &.active {
    background: rgba(var(--color-brand-1-rgb), 0.14);
    box-shadow:
      inset 0 0 0 1.5px rgba(var(--color-brand-1-rgb), 0.55),
      0 12px 28px rgba(var(--color-brand-1-rgb), 0.18);
    transform: translateY(-1px);

    .palette_name {
      color: var(--brand-color-pure);
    }
  }
}

.palette_preview_v2 {
  width: 100%;
  height: 38px;
  border-radius: var(--radius-m);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 0 6px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  transition: all var(--transition-base);

  .v2_key {
    width: 18px;
    height: 18px;
    border-radius: var(--radius-s);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

    &.accent {
      width: 22px;
      height: 22px;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
  }
}

.palette_name {
  font-size: 12px;
  font-weight: 700;
  color: var(--surface-text-primary);
  line-height: 1.4;
  word-break: break-word;
}

.color_control_card {
  border-radius: var(--radius-m);
  background: rgba(255, 255, 255, 0.78);
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: var(--transition-base);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  }

  .control_label {
    font-size: 12px;
    font-weight: 700;
    color: var(--surface-text-primary);
  }

  .control_picker {
    position: relative;
    width: 100%;
    height: 38px;
    border-radius: var(--radius-m);
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.08);
  }

  .control_swatch,
  .native_color_input {
    width: 100%;
    height: 100%;
    display: block;
  }

  .control_swatch {
    pointer-events: none;
  }

  .native_color_input {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
  }

  .native_hex_input {
    width: 100%;
    height: 36px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: var(--radius-m);
    background: var(--color-gray-1);
    padding: 0 10px;
    font-size: 12px;
    font-weight: 700;
    color: var(--surface-text-primary);
    text-transform: uppercase;
    transition: var(--transition-base);

    &:focus {
      outline: none;
      border-color: var(--brand-color-pure);
      box-shadow: 0 0 0 4px rgba(var(--color-brand-2-rgb), 0.1);
    }
  }
}

@media (max-width: 960px) {
  .keyboard_appearance_panel {
    padding: 14px;
  }

  .keyboard_appearance_primary_grid {
    grid-template-columns: 1fr;
  }

  .palette_grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .custom_color_grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .keyboard_appearance_section {
    padding: 14px;
  }
}
</style>
